import { IconError16 } from '@warp-ds/icons/react/error-16';
import { IconInfo16 } from '@warp-ds/icons/react/info-16';
import { Box } from '@warp-ds/react/components/box';
import { Pagination } from '@warp-ds/react/components/pagination';
import cn from 'classnames';
import { useEffect, useState } from 'react';

import { RangeSliderFilter } from 'shared/components/slider/RangeSliderFilter';
import { useConfig } from 'shared/config/ConfigContext';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { SortDetails } from 'shared/page/SortDetails';
import { EmptyWarning } from '@/components/emptyWarning/EmptyWarning';
import { FilterGroup } from '@/components/filters/FilterGroup';
import { PriceRangeFilter } from '@/components/filters/PriceRangeFilter';
import { DesktopTopBar } from '@/components/topBar/DesktopTopBar';
import { MobileTopBar } from '@/components/topBar/MobileTopBar';
import { trackLmsResultPage } from '@/lib/utils/tracking';
import { LmsDateFilter } from './filters/LmsDateFilter';
import { LmsDestinationFilter } from './filters/LmsDestinationFilter';
import { LmsOriginFilter } from './filters/LmsOriginFilter';
import { LmsRatingFilter } from './filters/LmsRatingFilter';
import { LmsSupplierFilter } from './filters/LmsSupplierFilter';
import { LmsTravelTypeFilter } from './filters/LmsTravelTypeFilter';
import { useLmsData } from './hooks/useLmsData';
import { useLmsDestination } from './hooks/useLmsDestination';
import { useLmsFilters } from './hooks/useLmsFilters';
import { LmsResultListItem } from './LmsResultListItem';
import { LmsSaveSearch } from './LmsSaveSearch';

type LmsResultPageLayoutProps = {
    isLoggedIn?: boolean;
};

export const LmsResultPageLayout = ({ isLoggedIn }: LmsResultPageLayoutProps) => {
    const { contextPathLMS } = useConfig();
    const { data, error, isLoading } = useLmsData();
    const { filters, updateFilters, resetFilters } = useLmsFilters();
    const destination = useLmsDestination();

    const [showFilters, setShowFilters] = useState<boolean>(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger tracking when the filters change.
    useEffect(() => {
        trackLmsResultPage(filters, data?.numFound);
    }, [filters]); // Only trigger tracking when the filters change. cant do it on offers because of swr will load data on page load

    const handlePageChange = (page: number) => {
        updateFilters('pageNumber', page === 1 ? undefined : page);
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    };

    const handleSortChange = (newSort?: string) => {
        updateFilters('sorter', newSort);
    };

    return (
        <>
            <SimpleHead
                title={`Restplasser - ${destination ?? 'sammenlign pris'} | FINN reise`}
                description="Finn billige restplasser fra alle reisebyrå på ett sted! På FINN.no kan du sammenligne priser og spare penger."
                canonical="https://www.finn.no/reise/restplasser"
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/restplasser">
                <MobileTopBar
                    vertical="lms"
                    numberOfOffers={data?.numFound}
                    filtersOpen={showFilters}
                    openFilter={setShowFilters}
                    sortOptions={data?.sortDropdownKeys}
                    defaultSort="pris_lms"
                    selectedSort={filters.sorter}
                    changeSort={handleSortChange}
                />
                <DesktopTopBar
                    vertical="lms"
                    numberOfOffers={data?.numFound}
                    sortOptions={data?.sortDropdownKeys}
                    defaultSort="pris_lms"
                    selectedSort={filters.sorter}
                    changeSort={handleSortChange}
                />
                <div className="md:grid md:grid-cols-3 md:gap-32 pb-32">
                    <section className={cn('divide-y-1', { 'hidden md:block': !showFilters })}>
                        <LmsSaveSearch filters={filters} isLoggedIn={isLoggedIn} />
                        <FilterGroup title="Avreisested" testId="originFilter">
                            <LmsOriginFilter data={data?.airportFacet} filters={filters} updateFilters={updateFilters} />
                        </FilterGroup>
                        <div data-testid="departureDateFilter" className="p-16 space-y-16">
                            <h3 className="h4">Dato</h3>
                            <LmsDateFilter filters={filters} updateFilters={updateFilters} />
                            <div className="text-xs s-text-subtle">Restplasser finnes kun for reiser nært frem i tid</div>
                        </div>
                        <FilterGroup title="Reiselengde" testId="durationFilter">
                            <RangeSliderFilter
                                id="durationRange"
                                title="Antall dager"
                                label={['Fra dager', 'Til dager']}
                                value={[filters.lengde?.from ?? 1, filters.lengde?.to ?? 30]}
                                min={1}
                                max={30}
                                renderValue={(val) => `${val} ${val === 1 ? 'dag' : 'dager'}`}
                                onChange={(value) => updateFilters('lengde', { from: value[0], to: value[1] })}
                                disabled={!data}
                            />
                        </FilterGroup>
                        <FilterGroup title="Reisetype" testId="travelTypeFilter">
                            <LmsTravelTypeFilter data={data?.typeFacet} filters={filters} updateFilters={updateFilters} />
                        </FilterGroup>
                        <FilterGroup title="Reisemål" testId="destinationFilter" className="space-y-16">
                            <LmsDestinationFilter data={data?.locationFacet} filters={filters} updateFilters={updateFilters} />
                        </FilterGroup>
                        <FilterGroup title="Pris per person" testId="priceFilter">
                            <PriceRangeFilter
                                id="priceRange"
                                legend="Pris per person"
                                labels={['Fra kroner', 'Til kroner']}
                                affix="kr"
                                range={[filters.pris?.from ?? null, filters.pris?.to ?? null]}
                                updateRange={(values) => updateFilters('pris', values ? { from: values[0], to: values[1] } : undefined)}
                            />
                        </FilterGroup>
                        <FilterGroup title="Hotellstandard" testId="ratingFilter">
                            <LmsRatingFilter filters={filters} updateFilters={updateFilters} />
                            <div className="text-xs s-text-subtle mt-12">Gjelder kun reiser med spesifisert bosted</div>
                        </FilterGroup>
                        <FilterGroup title="Tilbydere og selskaper" testId="supplierFilter" className="space-y-16">
                            <LmsSupplierFilter data={data?.supplierFacet} filters={filters} updateFilters={updateFilters} />
                        </FilterGroup>
                    </section>
                    <section className={cn('md:col-span-2', { 'hidden md:block': showFilters })}>
                        <h1 className="sr-only">Restplasser</h1>
                        <div className="md:hidden flex justify-between items-center py-16">
                            <SortDetails section="restplasser" />
                            <LmsSaveSearch small filters={filters} isLoggedIn={isLoggedIn} />
                        </div>
                        <Box as="section" info className="flex gap-16 mb-16">
                            <IconInfo16 className="s-text-link mt-2" />
                            <div className="flex-1 text-s" data-testid="disclaimer">
                                Prisene som oppgis er <strong>pris per person</strong> når to voksne reiser sammen og bor på samme rom i
                                billigste boalternativ. Husk at reiser kan ha endret pris eller blitt utsolgt innen du fullfører
                                bestillingen.
                            </div>
                        </Box>
                        <section id="resultList" data-testid="resultList" className="relative space-y-16">
                            {error && (
                                <Box className="flex gap-16 s-bg-negative-subtle">
                                    <IconError16 className="s-text-negative mt-2" />
                                    <span data-testid="resultsError">
                                        Ooops... En feil oppsto ved henting av nye tilbud, vennligst prøv igjen.
                                    </span>
                                </Box>
                            )}
                            {data?.offers?.length ? null : <EmptyWarning id="results" resetFilters={resetFilters} />}
                            {data?.offers?.map((offer) => (
                                <LmsResultListItem key={offer.offerId} offer={offer} isLoading={isLoading} />
                            ))}
                        </section>
                        {data?.currentPage && data?.totalPages > 1 ? (
                            <Pagination
                                createHref={(p: number) => `${contextPathLMS}?pageNumber=${p}`}
                                currentPage={data.currentPage}
                                lastPage={data.totalPages}
                                onChange={handlePageChange}
                            />
                        ) : null}
                    </section>
                </div>
            </Main>
        </>
    );
};
